/**
 * Presentational primitives for the metrics page.
 *
 * Form choices follow the data's job rather than habit:
 *   - a single headline value is a HERO FIGURE, not a one-bar bar chart
 *   - a handful of headline numbers is a KPI ROW of stat tiles
 *   - a single ratio against a limit is a METER (same-ramp track), not a gauge or a
 *     two-slice pie
 *   - a rate inside a dense table gets a MICRO-BAR beside the number, so an outlier is
 *     visible without reading digits
 *
 * Marks follow fixed specs: bars cap at 24px with a 4px rounded data-end, gridlines are
 * solid hairlines, and text never wears a series color - identity comes from a swatch
 * beside the label.
 */

import type { ReactNode } from "react";
import { HiArrowUp, HiArrowDown } from "react-icons/hi";
import type { Rate } from "../../services/sales-metrics";
import { formatRate } from "./format";

/* -------------------------------------------------------------------------- */
/* Hero figure - exactly one per view                                          */
/* -------------------------------------------------------------------------- */

export function HeroFigure({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </span>
      {/* Proportional figures: tabular-nums makes a large number look loose. */}
      <span className="viz-hero">{value}</span>
      {caption ? (
        <span className="text-xs text-gray-500 dark:text-gray-400">{caption}</span>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Stat tile                                                                    */
/* -------------------------------------------------------------------------- */

export interface StatTileProps {
  label: string;
  value: string;
  /** Optional signed delta vs a named period. */
  delta?: { text: string; direction: "up" | "down" | "flat" };
  /** Whether an increase is a good thing (cancel rate: no). Drives delta color. */
  upIsGood?: boolean;
  /** Sub-label, typically the denominator a rate was computed from. */
  caption?: string;
  /** 12-point sparkline series. */
  sparkline?: number[];
}

/**
 * KPI tile. Borrows the dashboard revenue card's vocabulary - bordered, unshadowed
 * (the documented exception to the app's shadowed cards), green-up/red-down delta -
 * but NOT its 140/180/200 min-height ladder. This page shows six tiles plus three
 * charts above the fold; the dashboard shows four tiles. Height is content-driven so
 * a tile without a sparkline does not reserve space for one.
 */
export function StatTile({
  label,
  value,
  delta,
  upIsGood = true,
  caption,
  sparkline,
}: StatTileProps) {
  const isFlat = !delta || delta.direction === "flat";
  const isGood = !isFlat && (delta.direction === "up") === upIsGood;
  const deltaClass = isFlat
    ? "text-gray-500 dark:text-gray-400"
    : isGood
      ? "text-green-500"
      : "text-red-500";

  // Trend drives the sparkline stroke, matching the dashboard's green-up/red-down.
  const trendUp =
    sparkline && sparkline.length > 1
      ? sparkline[sparkline.length - 1]! >= sparkline[0]!
      : true;

  return (
    <div className="h-full rounded-lg border border-gray-100 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 md:p-4">
      <div className="flex h-full flex-col justify-between">
        <div className="space-y-1">
          <span className="block text-left text-xs text-gray-500 dark:text-gray-400">
            {label}
          </span>

          <div className="space-y-0.5">
            <div className="flex items-center justify-between">
              <span className="block text-left text-xl font-bold text-gray-900 dark:text-white">
                {value}
              </span>
              {delta ? (
                /* Direction is carried by the arrow icon as well as the color, so the
                   meaning survives for a colorblind reader. */
                <div className="ml-1 flex items-center whitespace-nowrap sm:ml-2">
                  {!isFlat ? (
                    delta.direction === "up" ? (
                      <HiArrowUp
                        className={`mr-0.5 h-3 w-3 shrink-0 sm:mr-1 sm:h-4 sm:w-4 ${deltaClass}`}
                        aria-hidden="true"
                      />
                    ) : (
                      <HiArrowDown
                        className={`mr-0.5 h-3 w-3 shrink-0 sm:mr-1 sm:h-4 sm:w-4 ${deltaClass}`}
                        aria-hidden="true"
                      />
                    )
                  ) : null}
                  <span className={`text-xs font-medium sm:text-sm ${deltaClass}`}>
                    {delta.text}
                  </span>
                </div>
              ) : null}
            </div>

            {caption ? (
              <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                {caption}
              </div>
            ) : null}
          </div>
        </div>

        {sparkline && sparkline.length > 1 ? (
          <div className="mt-2">
            <Sparkline values={sparkline} up={trendUp} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 12-point sparkline. Inline SVG rather than ApexCharts (far lighter for a 12-point
 * line), but styled like the dashboard's: green when the trend is up, red when down,
 * with a soft gradient beneath and an h-8/sm:h-12 box.
 */
function Sparkline({ values, up }: { values: number[]; up: boolean }) {
  const width = 120;
  const height = 32;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const stroke = up ? "#10b981" : "#ef4444";
  const gradientId = `spark-${up ? "up" : "down"}`;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    // Inset by the stroke width so the line is never clipped at the extremes.
    const y = 2 + (height - 4) - ((v - min) / span) * (height - 4);
    return [x, y] as const;
  });

  const path = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const area = `${path} L${width},${height} L0,${height} Z`;

  return (
    <div className="h-6">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        className="h-full w-full"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.5" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradientId})`} stroke="none" />
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Meter - a single ratio against a limit                                       */
/* -------------------------------------------------------------------------- */

export function Meter({
  label,
  ratio,
  caption,
}: {
  label: string;
  /** 1 = target met. Values above 1 are clamped for the fill but shown in the text. */
  ratio: Rate;
  caption?: string;
}) {
  const pct = ratio === null ? 0 : Math.max(0, Math.min(ratio, 1)) * 100;

  // Fill carries severity. The dashboard's forecast bar is a flat blue-600, but this
  // meter is GRADED - the color is information, not decoration - so it keeps the
  // four-step ramp while adopting the dashboard's track shape and semantic colors.
  const fill =
    ratio === null
      ? "var(--viz-de-emphasis)"
      : ratio >= 1
        ? "var(--viz-status-good)"
        : ratio >= 0.7
          ? "var(--viz-series-1)"
          : ratio >= 0.4
            ? "var(--viz-status-warning)"
            : "var(--viz-status-critical)";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </span>
        <span className="viz-tabular text-sm font-semibold text-gray-900 dark:text-white">
          {formatRate(ratio, 0)}
        </span>
      </div>

      <div
        className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700"
        role="meter"
        aria-valuenow={ratio === null ? undefined : Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: fill }} />
      </div>

      {caption ? (
        <span className="text-xs text-gray-500 dark:text-gray-400">{caption}</span>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Micro-bar cell - a rate inside a dense table                                 */
/* -------------------------------------------------------------------------- */

export function MicroBarCell({
  rate,
  benchmark,
  max = 1,
}: {
  rate: Rate;
  /** Team or company median, drawn as a hairline marker on the track. */
  benchmark?: number | null;
  /** Upper bound of the track. Rates cap at 1; revenue-style values pass their own. */
  max?: number;
}) {
  if (rate === null) {
    return <span className="text-xs text-gray-500 dark:text-gray-400">—</span>;
  }

  const pct = Math.max(0, Math.min(rate / max, 1)) * 100;
  const benchmarkPct =
    benchmark === null || benchmark === undefined
      ? null
      : Math.max(0, Math.min(benchmark / max, 1)) * 100;

  const below = benchmark !== null && benchmark !== undefined && rate < benchmark;

  return (
    <div className="flex items-center gap-2">
      <div className="relative h-2 w-16 shrink-0 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: below ? "var(--viz-status-serious)" : "var(--viz-series-1)",
          }}
        />
        {benchmarkPct !== null ? (
          /* Hairline benchmark marker - the reference the reader compares against. */
          <div
            className="absolute top-0 h-full w-px bg-gray-600 dark:bg-gray-300"
            style={{ left: `${benchmarkPct}%` }}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <span className="viz-tabular text-xs text-gray-900 dark:text-white">
        {formatRate(rate)}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Chart card + table-view toggle                                               */
/* -------------------------------------------------------------------------- */

/**
 * Card shell, matching the dashboard's large cards (shadow-md with a hover lift and
 * the p-4/sm:p-6/xl:p-8 padding ladder).
 *
 * Two heading tiers, because this page fits far more cards per screen than the
 * dashboard does:
 *   - "section" (default): a full-width card, styled exactly like a dashboard card
 *   - "paired": a card sharing a row inside a lg:grid-cols-2 grid, one step down
 * Rule for anything added later: full-width -> section, half-width -> paired.
 */
export function ChartCard({
  title,
  subtitle,
  actions,
  level = "section",
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  level?: "section" | "paired";
  children: ReactNode;
}) {
  const isSection = level === "section";

  return (
    <section className="flex h-full flex-col gap-2 rounded-lg bg-white p-3 shadow-md transition-shadow hover:shadow-lg dark:bg-gray-800 sm:p-4">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col">
          <h3
            className={
              isSection
                ? "text-base font-bold text-gray-900 dark:text-white md:text-lg"
                : "text-sm font-semibold text-gray-900 dark:text-white"
            }
          >
            {title}
          </h3>
          {subtitle ? (
            <p className="text-xs font-normal text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          ) : null}
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}

/** Legend swatch. Text stays in an ink token; only the swatch carries the hue. */
export function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
      <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: color }} aria-hidden="true" />
      {label}
    </span>
  );
}
